import { useQuery } from "@tanstack/react-query";
import qs from "qs";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "reactstrap";
import toast from 'react-hot-toast';
import React, { useEffect, useState, useRef } from "react";
import Header from "./Header";
import SearchForm from "./SearchForm";
import NonDomesticConsumerTable from "./NonDomesticConsumerTable";
import NonDomesticConsumerModal from "./NonDomesticConsumerModal";
import { handleGetNonDomesticConsumers, handleDeleteNonDomesticConsumer } from "../../services/nondomesticconsumer.service";
import SvgIcons from "../../components/SVGIcons"
import { getModulePermissions, isAdminUser } from "../../utils/authorisedmodule";
import Swal from "sweetalert2";

export default function NonDomesticConsumerList(){

    const location = useLocation();
    const navigate = useNavigate();
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });

    const [pagination, setPagination] = useState({
        pageIndex: params.page ? parseInt(params.page) - 1 : 0,
        pageSize: params.limit ? parseInt(params.limit) : 10,
    });

    const [modal, setModal] = useState(false);

    const [sorting, setSorting] = useState([]);
    
    const [filter, setFilter] = useState({
        search: "",
        district: "",
        state: "",
        customerLevel: "",
        supplyTo: "",
        category: ""
    });
    
    const permission = useRef({ addModule : false, editModule : false, deleteModule : false });
    useEffect(()=>{
        isAdminUser().then(response=>{
            if(response){
                permission.current.addModule = true;
                permission.current.editModule = true;
                permission.current.deleteModule = true;
            }else{
                getModulePermissions("Consumer").then(response => {
                    if(response.includes("CONSUMER_CREATE")){
                        permission.current.addModule = true;
                    }
                    if(response.includes("CONSUMER_UPDATE")){
                        permission.current.editModule = true;
                    }
                    if(response.includes("CONSUMER_DELETE")){
                        permission.current.deleteModule = true;
                    }
                }).catch(error => {
                    console.error("Error fetching permissions:", error);
                });
            }
        }).catch(error=>{
            console.error("Error get during to fetch User Type", error);
        })
        
    },[]);

    const toggle = () => setModal(!modal);

    const editNonDomesticConsumer = async (id) => {
        navigate(`/non-domestic-consumers/edit/${id}`);
    };
    
    const deleteNonDomesticConsumer = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "Are you sure you want to delete this consumer?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "No, cancel!",
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                try {
                    handleDeleteNonDomesticConsumer(id).then((responseDelete) => {
                        toast.success("Consumer deleted successfully");
                        dataQuery.refetch();
                    }).catch((error) => {
                        toast.error(error.response.data.detail);
                    })
                } catch (error) {
                    toast.error(error.response.data.detail);
                }
            } else {

            }
        })
    };

    const dataQuery = useQuery({
        queryKey: ["data", pagination, sorting, filter],
        queryFn: () => {
            const filterObj = qs.parse(qs.stringify(filter, { skipNulls: true }));
            Object.keys(filterObj).forEach(key => filterObj[key] === "" && delete filterObj[key]);

            if (sorting.length === 0) {
                return handleGetNonDomesticConsumers({
                    page: pagination.pageIndex,
                    size: pagination.pageSize,
                    ...filterObj,
                });
            } else {
                return handleGetNonDomesticConsumers({
                    page: pagination.pageIndex,
                    size: pagination.pageSize,
                    sort: sorting
                        .map(
                            (sort) => `${sort.id},${sort.desc ? "desc" : "asc"}`
                        )
                        .join(","),
                    ...filterObj,
                });
            }
        },
    });

    useEffect(() => {
        if (dataQuery.data?.data?.totalPages < pagination.pageIndex + 1) {
            setPagination({
                pageIndex: dataQuery.data?.data?.totalPages - 1,
                pageSize: 10,
            });
        }
    }, [dataQuery.data?.data?.totalPages]);

    const columns = React.useMemo(
        () => [
            {
                accessorFn: (row) => row.consumerName,
                id: "consumerName",
                header: () => "Consumer Name",
            },
            {
                accessorFn: (row) => row.consumerKey,
                id: "consumerKey",
                header: () => "Consumer Key",
            },
            // {
            //     accessorFn: (row) => row.categoryName,
            //     id: "consumerCategory",
            //     header: () => "Consumer Category",
            // },
            {
                accessorFn: (row) => row.consumerType,
                id: "furtherSupplyTo",
                header: () => "Further Supply to",
            },
            {
                accessorFn: (row) => row.gstRegistrationNo,
                id: "gstNumber",
                header: () => "GST Number",
            },
            {
                accessorFn: (row) => row.udyogAadharNumber,
                id: "udyogAadharNumber",
                header: () => "Udyog Aadhar Number",
            },
            {
                accessorFn: (row) => row.sapErpCode,
                id: "sapErpCode",
                header: () => "SAP/ERP Code",
            },
            {
                accessorFn: (row) => row.stateName,
                id: "stateName",
                header: () => "Status",
            },
            {
                accessorFn: (row) => row.districtName,
                id: "district",
                header: () => "District",
            },
            {
                accessorFn: (row) => row.customerLevel,
                id: "customerLevel",
                header: () => "Customer Level",
            },
            {
                accessorFn: (row) => row.latitude,
                id: "latitude",
                header: () => "Latitude",
            },
            {
                accessorFn: (row) => row.longitude,
                id: "longitude",
                header: () => "Longitude",
            },
            {
                accessorFn: (row) => row.pan,
                id: "pan",
                header: () => "PAN",
            },
            {
                accessorFn: (row) => row.supplyThrough?row.supplyThrough.title:'',
                id: "supplyThrough",
                header: () => "Supply Through",
            },
            {
                accessorFn: (row) => row.aadharNumber,
                id: "aadharNumber",
                header: () => "Aadhar Number",
            },
            {
                id: "actions",
                isAction: true,
                cell: (info) => {
                    return (
                        <div className="d-flex items-center gap-2 justify-content-center">
                            { permission.current.editModule ? 
                            <div
                                onClick={() => {
                                    editNonDomesticConsumer(info.row.original.id);
                                }}
                            >
                                <span className=''>{SvgIcons.editIcon}</span>
                            </div> : "" }
                            { permission.current.deleteModule ? 
                            <div
                                onClick={() => {
                                    deleteNonDomesticConsumer(info.row.original.id);
                                }}
                            >
                                <span className=''>{SvgIcons.deleteIcon}</span>
                            </div> : "" }
                        </div>
                    );
                },
                header: () => <div className="d-flex justify-content-center">Actions</div>,
                enableSorting: false,
            },
        ],
        []
    );

    useEffect(() => {
        setPagination({
            pageIndex: 0,
            pageSize: 10,
        });
    }, [filter]);

    return (
        <React.Fragment>
            <div className="d-flex flex-column pageContainer p-3 h-100 overflow-auto">
            {permission.current.addModule?<Header toggle={toggle} />:""}
                <div className="flex-grow-1 pageContent position-relative pt-4 overflow-auto">
                    <Card className="h-100 bg-white shadow-lg border-0 theme-card-cover">
                        <SearchForm filter={filter} setFilter={setFilter} />
                        <NonDomesticConsumerTable
                            columns={columns}
                            dataQuery={dataQuery}
                            pagination={pagination}
                            setPagination={setPagination}
                            sorting={sorting}
                            setSorting={setSorting}
                        />
                    </Card>
                </div>
            </div>
            <NonDomesticConsumerModal modal={modal} toggle={toggle} />
        </React.Fragment>
    );
}


